import React, { useEffect, useMemo, useState } from "react";
import API from "./api";
import { getProfileNumberDisplay, PROFILE_NO_DISPLAY_SORT_KEY } from "./profileNumber";

const PAGE_SIZE = 24;

export default function AssignedProfiles({
  onBack,
  onViewProfile,
  onSwitchToAssignedByMe,
  canViewProfile = true,
}) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ male: 0, female: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState("assigned_latest");
  const [viewMode, setViewMode] = useState("grid");

  useEffect(() => {
    loadAssignedProfiles(currentPage, sortBy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, sortBy]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const headerStats = useMemo(
    () => [
      { label: "Assigned", value: total, icon: <AssignedProfilesIcon size={22} color={C.accent} /> },
      { label: "Male", value: stats.male, icon: <MaleIcon size={22} color={C.accent} /> },
      { label: "Female", value: stats.female, icon: <FemaleIcon size={22} color={C.accent} /> },
    ],
    [stats, total]
  );

  async function loadAssignedProfiles(page = 1, sortValue = sortBy) {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({
        include_meta: "1",
        limit: String(PAGE_SIZE),
        offset: String(Math.max(0, (page - 1) * PAGE_SIZE)),
      });

      if (sortValue === "profile_oldest") {
        params.set("sort_by", PROFILE_NO_DISPLAY_SORT_KEY);
        params.set("sort_dir", "asc");
      } else if (sortValue === "name") {
        params.set("sort_by", "full_name");
        params.set("sort_dir", "asc");
      } else if (sortValue === "age_asc") {
        params.set("sort_by", "age");
        params.set("sort_dir", "asc");
      } else if (sortValue === "age_desc") {
        params.set("sort_by", "age");
        params.set("sort_dir", "desc");
      } else if (sortValue === "profile_latest") {
        params.set("sort_by", PROFILE_NO_DISPLAY_SORT_KEY);
        params.set("sort_dir", "desc");
      } else {
        params.set("sort_by", "assigned_at");
        params.set("sort_dir", "desc");
      }

      const res = await API.get(`/profiles/assigned-to-me?${params.toString()}`);
      const payload = Array.isArray(res.data) ? { items: res.data, total: res.data.length } : (res.data || {});
      const nextItems = safeArray(payload.items);
      const nextTotal = Number(payload.total || 0);
      const nextTotalPages = Math.max(1, Math.ceil(nextTotal / PAGE_SIZE));

      if (page > nextTotalPages) {
        setCurrentPage(nextTotalPages);
        return;
      }

      setItems(nextItems);
      setTotal(nextTotal);
      setStats({
        male: Number(payload.stats?.male || 0),
        female: Number(payload.stats?.female || 0),
      });
    } catch (err) {
      console.error("Failed to load assigned profiles", err);
      setError(err.response?.data?.error || "Failed to load assigned profiles.");
      setItems([]);
      setTotal(0);
      setStats({ male: 0, female: 0 });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={S.page}>
      <style>{`
        @media (max-width: 860px) {
          .jk-assigned-hero {
            flex-direction: column !important;
            align-items: flex-start !important;
          }
          .jk-assigned-stat-grid {
            grid-template-columns: 1fr !important;
          }
        }
        @media (max-width: 720px) {
          .jk-assigned-results-grid {
            grid-template-columns: 1fr !important;
          }
          .jk-assigned-list-card {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>

      <div style={S.hero} className="jk-assigned-hero">
        <div>
          <p style={S.eyebrow}>Assignment Workspace</p>
          <h1 style={S.title}>Assigned To Me</h1>
          <p style={S.subtitle}>
            Review only the profiles currently assigned to your account and open them directly from here.
          </p>
        </div>

        <div style={S.heroActions}>
          <button type="button" style={S.backBtn} onClick={onBack}>
            Back to Search
          </button>

          <div style={S.toolbarCard}>
            <div style={S.toolbarGroup}>
              <span style={S.toolbarLabel}>Sort by</span>
              <select style={S.toolbarSelect} value={sortBy} onChange={(event) => {
                setCurrentPage(1);
                setSortBy(event.target.value);
              }}>
                <option value="assigned_latest">Latest Assigned</option>
                <option value="profile_latest">Latest Profile Number</option>
                <option value="profile_oldest">Oldest Profile Number</option>
                <option value="name">Name A-Z</option>
                <option value="age_asc">Age Low-High</option>
                <option value="age_desc">Age High-Low</option>
              </select>
            </div>

            <div style={S.toolbarDivider} />

            <div style={S.toolbarGroup}>
              <span style={S.toolbarLabel}>View</span>
              <div style={S.viewToggle}>
                <button
                  type="button"
                  style={{ ...S.viewToggleBtn, ...(viewMode === "grid" ? S.viewToggleActive : {}) }}
                  onClick={() => setViewMode("grid")}
                >
                  Grid
                </button>
                <button
                  type="button"
                  style={{ ...S.viewToggleBtn, ...(viewMode === "list" ? S.viewToggleActive : {}) }}
                  onClick={() => setViewMode("list")}
                >
                  List
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={S.tabBar}>
        <div style={S.tabRow}>
          <button type="button" style={{ ...S.tabBtn, ...S.tabBtnActive }}>
            Assigned To Me
          </button>
          <button
            type="button"
            style={S.tabBtn}
            onClick={typeof onSwitchToAssignedByMe === "function" ? onSwitchToAssignedByMe : undefined}
          >
            Assigned By Me
          </button>
        </div>
      </div>

      <div style={S.statGrid} className="jk-assigned-stat-grid">
        {headerStats.map((item) => (
          <div key={item.label} style={S.statCard}>
            <span style={S.statBadge}>{item.icon}</span>
            <div style={S.statContent}>
              <b style={S.statValue}>{item.value}</b>
              <span style={S.statLabel}>{item.label}</span>
            </div>
          </div>
        ))}
      </div>

      {error && <div style={S.errorBox}>{error}</div>}

      {loading ? (
        <Splash
          title="Loading assigned profiles"
          subtitle="Pulling your current profile assignments."
        />
      ) : items.length === 0 ? (
        <Splash
          title="No assigned profiles"
          subtitle="There are no active profile allocations on your account right now."
        />
      ) : (
        <>
          <div
            style={viewMode === "grid" ? S.grid : S.list}
            className={viewMode === "grid" ? "jk-assigned-results-grid" : ""}
          >
            {items.map((profile) => (
              <AssignedProfileCard
                key={profile.id}
                profile={profile}
                mode={viewMode}
                canViewProfile={canViewProfile}
                onView={() => {
                  if (typeof onViewProfile === "function") {
                    onViewProfile(profile.id);
                  }
                }}
              />
            ))}
          </div>

          <div style={S.paginationBar}>
            <button
              type="button"
              style={{ ...S.paginationBtn, ...(currentPage <= 1 ? S.paginationBtnDisabled : {}) }}
              disabled={currentPage <= 1 || loading}
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
            >
              Previous
            </button>

            <span style={S.paginationInfo}>
              Page {currentPage} of {totalPages} | {total} assigned profiles
            </span>

            <button
              type="button"
              style={{ ...S.paginationBtn, ...(currentPage >= totalPages ? S.paginationBtnDisabled : {}) }}
              disabled={currentPage >= totalPages || loading}
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function AssignedProfileCard({ profile, mode, onView, canViewProfile }) {
  const [imgError, setImgError] = useState(false);
  const fullName = getFullName(profile);
  const initials = `${profile.first_name?.[0] || ""}${profile.last_name?.[0] || ""}`.toUpperCase();
  const avatarBg = pickColor(profile.first_name || "A");
  const age = getAge(profile.birth_date || profile.dob);
  const location = profile.location_display_cache || [profile.city, profile.state].filter(Boolean).join(", ");
  const education = profile.education_summary || "";
  const occupation = profile.occupation_type || "";
  const assignedAt = formatDateTime(profile.assigned_at);
  const assignedBy = formatAssignedBy(profile);
  const showImage = Boolean(profile.photo || profile.primary_photo_url_cache) && !imgError;
  const imageUrl = profile.photo || profile.primary_photo_url_cache;

  if (mode === "list") {
    return (
      <div
        style={S.listCard}
        className="jk-assigned-list-card"
        onClick={canViewProfile ? onView : undefined}
      >
        <div style={S.listMedia}>
          {showImage ? (
            <img src={imageUrl} alt={fullName} style={S.listImage} onError={() => setImgError(true)} />
          ) : (
            <div style={{ ...S.listAvatar, background: avatarBg }}>
              <span style={S.listInitials}>{initials || "?"}</span>
            </div>
          )}
        </div>

        <div style={S.listBody}>
          <div style={S.profileTopRow}>
            <span style={S.profileNo}>#{getProfileNumberDisplay(profile, "-")}</span>
            {assignedAt ? <span style={S.assignedAtBadge}>Assigned {assignedAt}</span> : null}
          </div>
          {assignedBy ? <div style={S.assignmentMeta}>Assigned by {assignedBy}</div> : null}
          <h3 style={S.profileName}>{fullName}</h3>
          <div style={S.metaRow}>
            {age ? <span style={S.metaItem}>{age} yrs</span> : null}
            {profile.gender ? <span style={S.metaItem}>{profile.gender}</span> : null}
            {profile.marital_status ? <span style={S.metaItem}>{profile.marital_status}</span> : null}
          </div>
          {location ? <div style={S.locationText}>{location}</div> : null}
          {(education || occupation) ? (
            <div style={S.infoStack}>
              {education ? <span>{education}</span> : null}
              {occupation ? <span>{occupation}</span> : null}
            </div>
          ) : null}
        </div>

        {canViewProfile ? (
          <button
            type="button"
            style={S.viewBtn}
            onClick={(event) => {
              event.stopPropagation();
              onView();
            }}
          >
            View Profile
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div
      style={{ ...S.card, ...(canViewProfile ? S.cardClickable : {}) }}
      onClick={canViewProfile ? onView : undefined}
    >
      <div style={S.cardMedia}>
        {showImage ? (
          <img src={imageUrl} alt={fullName} style={S.cardImage} onError={() => setImgError(true)} />
        ) : (
          <div style={{ ...S.cardAvatar, background: avatarBg }}>
            <span style={S.cardInitials}>{initials || "?"}</span>
          </div>
        )}
        <div style={S.cardOverlay}>
          <span style={S.profileNo}>#{getProfileNumberDisplay(profile, "-")}</span>
          <div style={S.overlayMetaStack}>
            {assignedAt ? <span style={S.assignedAtBadge}>Assigned {assignedAt}</span> : null}
            {assignedBy ? <span style={S.assignedByBadge}>By {assignedBy}</span> : null}
          </div>
        </div>
      </div>

      <div style={S.cardBody}>
        <h3 style={S.profileName}>{fullName}</h3>
        <div style={S.metaRow}>
          {age ? <span style={S.metaItem}>{age} yrs</span> : null}
          {profile.gender ? <span style={S.metaItem}>{profile.gender}</span> : null}
          {profile.marital_status ? <span style={S.metaItem}>{profile.marital_status}</span> : null}
        </div>
        {location ? <div style={S.locationText}>{location}</div> : null}
        {(education || occupation) ? (
          <div style={S.infoStack}>
            {education ? <span>{education}</span> : null}
            {occupation ? <span>{occupation}</span> : null}
          </div>
        ) : null}
        {canViewProfile ? (
          <button
            type="button"
            style={S.viewBtn}
            onClick={(event) => {
              event.stopPropagation();
              onView();
            }}
          >
            View Profile
          </button>
        ) : null}
      </div>
    </div>
  );
}

function Splash({ title, subtitle }) {
  return (
    <div style={S.splash}>
      <div style={S.splashOrb} />
      <p style={S.splashTitle}>{title}</p>
      <p style={S.splashSub}>{subtitle}</p>
    </div>
  );
}

function AssignedProfilesIcon({ size = 20, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="4" y="4.5" width="11" height="15" rx="2.5" stroke={color} strokeWidth="1.8" />
      <path d="M8 9h3.8M8 12.5h3.8M8 16h2.4" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="18" cy="9" r="2.7" stroke={color} strokeWidth="1.8" />
      <path d="M14.8 18c.7-1.8 2.1-2.8 3.8-2.8 1.7 0 3.1 1 3.8 2.8" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function MaleIcon({ size = 20, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="9" cy="15" r="5" stroke={color} strokeWidth="1.8" />
      <path d="M13 11l6-6" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      <path d="M14.5 5H19v4.5" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function FemaleIcon({ size = 20, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="8" r="4.5" stroke={color} strokeWidth="1.8" />
      <path d="M12 12.5v8" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      <path d="M8.5 17h7" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function getFullName(profile) {
  return profile.full_name || [profile.first_name, profile.middle_name, profile.last_name].filter(Boolean).join(" ") || "Unnamed Profile";
}

function getAge(dateValue) {
  if (!dateValue) return null;
  const dob = new Date(dateValue);
  if (Number.isNaN(dob.getTime())) return null;
  return Math.floor((new Date() - dob) / (365.25 * 86400000));
}

function pickColor(str) {
  const palette = ["#D73C2E", "#B9343A", "#C7433F", "#A93338", "#D1483A", "#252935"];
  let hash = 0;
  for (const char of str) hash = char.charCodeAt(0) + ((hash << 5) - hash);
  return palette[Math.abs(hash) % palette.length];
}

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatAssignedBy(profile) {
  if (profile?.assigned_by_email) {
    return String(profile.assigned_by_email).split("@")[0];
  }
  if (profile?.assigned_by_user_id) {
    return `user #${profile.assigned_by_user_id}`;
  }
  return "";
}

const C = {
  bg: "#fff9f7",
  card: "#ffffff",
  border: "rgba(185,52,58,0.12)",
  accent: "#D73C2E",
  accentDark: "#B9343A",
  accentLight: "#fff2ef",
  text: "#252935",
  muted: "#6b6f7d",
  shadow: "0 22px 55px rgba(215,60,46,0.10)",
};

const S = {
  page: {
    display: "flex",
    flexDirection: "column",
    gap: 18,
  },
  hero: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 18,
    padding: "26px 30px",
    borderRadius: 32,
    background: "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(255,245,242,0.96) 100%)",
    border: `1px solid ${C.border}`,
    boxShadow: C.shadow,
  },
  eyebrow: {
    margin: 0,
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: "0.24em",
    textTransform: "uppercase",
    color: C.accent,
  },
  tabRow: {
    display: "inline-flex",
    gap: 8,
    padding: 6,
    borderRadius: 999,
    background: "#fff3ef",
    border: `1px solid ${C.border}`,
  },
  tabBar: {
    padding: 6,
    borderRadius: 999,
    background: "#fff7f4",
    border: `1px solid ${C.border}`,
    boxShadow: "0 14px 34px rgba(215,60,46,0.08)",
  },
  tabBtn: {
    border: "none",
    background: "transparent",
    color: C.muted,
    borderRadius: 999,
    padding: "10px 16px",
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
  },
  tabBtnActive: {
    background: C.accent,
    color: "#ffffff",
    boxShadow: "0 10px 24px rgba(215,60,46,0.20)",
  },
  title: {
    margin: "10px 0 8px",
    fontSize: 46,
    lineHeight: 1,
    color: C.text,
  },
  subtitle: {
    margin: 0,
    color: C.muted,
    fontSize: 18,
    lineHeight: 1.55,
    maxWidth: 680,
  },
  heroActions: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 14,
    minWidth: 320,
  },
  backBtn: {
    border: `1px solid ${C.border}`,
    background: "#ffffff",
    color: C.text,
    borderRadius: 999,
    padding: "12px 18px",
    fontSize: 14,
    fontWeight: 800,
    cursor: "pointer",
  },
  toolbarCard: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    padding: "14px 18px",
    borderRadius: 22,
    background: "#ffffff",
    border: `1px solid ${C.border}`,
  },
  toolbarGroup: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  toolbarLabel: {
    fontSize: 13,
    fontWeight: 800,
    color: C.muted,
  },
  toolbarSelect: {
    borderRadius: 999,
    border: `1px solid ${C.border}`,
    padding: "10px 14px",
    fontSize: 14,
    fontWeight: 700,
    color: C.text,
    background: "#ffffff",
  },
  toolbarDivider: {
    width: 1,
    height: 34,
    background: C.border,
  },
  viewToggle: {
    display: "inline-flex",
    padding: 4,
    borderRadius: 999,
    background: C.accentLight,
    gap: 4,
  },
  viewToggleBtn: {
    border: "none",
    background: "transparent",
    color: C.muted,
    padding: "9px 14px",
    borderRadius: 999,
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
  },
  viewToggleActive: {
    background: C.accent,
    color: "#ffffff",
  },
  statGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 14,
  },
  statCard: {
    background: "#ffffff",
    border: `1px solid ${C.border}`,
    borderRadius: 24,
    padding: "18px 20px",
    boxShadow: "0 16px 40px rgba(215,60,46,0.08)",
    display: "flex",
    alignItems: "center",
    gap: 14,
  },
  statBadge: {
    width: 48,
    height: 48,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #fff2ef 0%, #fffaf8 100%)",
    color: C.accent,
    border: `1px solid ${C.border}`,
    flexShrink: 0,
  },
  statContent: {
    display: "flex",
    flexDirection: "column",
    gap: 3,
  },
  statValue: {
    color: C.accentDark,
    fontSize: 34,
    fontWeight: 950,
    lineHeight: 1,
  },
  statLabel: {
    color: C.muted,
    fontSize: 13,
    fontWeight: 800,
  },
  errorBox: {
    padding: "14px 16px",
    borderRadius: 20,
    background: "#fff4f1",
    border: `1px solid ${C.border}`,
    color: C.accentDark,
    fontWeight: 700,
  },
  splash: {
    position: "relative",
    overflow: "hidden",
    minHeight: 240,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    gap: 10,
    background: "#ffffff",
    borderRadius: 28,
    border: `1px solid ${C.border}`,
    boxShadow: C.shadow,
    padding: 28,
  },
  splashOrb: {
    position: "absolute",
    width: 180,
    height: 180,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(215,60,46,0.12) 0%, rgba(215,60,46,0) 72%)",
    top: -70,
    right: -40,
  },
  splashTitle: {
    margin: 0,
    fontSize: 26,
    fontWeight: 900,
    color: C.text,
  },
  splashSub: {
    margin: 0,
    maxWidth: 520,
    color: C.muted,
    fontSize: 15,
    lineHeight: 1.6,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 18,
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  card: {
    background: "#ffffff",
    borderRadius: 28,
    overflow: "hidden",
    border: `1px solid ${C.border}`,
    boxShadow: C.shadow,
  },
  cardClickable: {
    cursor: "pointer",
  },
  cardMedia: {
    position: "relative",
    height: 250,
    background: "#252935",
  },
  cardImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  cardAvatar: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  cardInitials: {
    color: "#ffffff",
    fontSize: 72,
    fontWeight: 900,
    letterSpacing: "0.04em",
  },
  cardOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: 14,
    background: "linear-gradient(180deg, rgba(12,14,20,0.14) 0%, rgba(12,14,20,0.02) 38%, rgba(12,14,20,0.16) 100%)",
  },
  overlayMetaStack: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 8,
  },
  profileNo: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "8px 12px",
    background: "rgba(215,60,46,0.95)",
    color: "#ffffff",
    fontSize: 13,
    fontWeight: 900,
  },
  assignedAtBadge: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "8px 12px",
    background: "rgba(255,255,255,0.94)",
    color: C.text,
    fontSize: 12,
    fontWeight: 800,
  },
  assignedByBadge: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "6px 10px",
    background: "rgba(37,41,53,0.82)",
    color: "#ffffff",
    fontSize: 11,
    fontWeight: 800,
  },
  cardBody: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    padding: 20,
  },
  profileName: {
    margin: 0,
    fontSize: 22,
    lineHeight: 1.15,
    color: C.text,
  },
  metaRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
  },
  metaItem: {
    color: C.muted,
    fontSize: 14,
    fontWeight: 800,
  },
  assignmentMeta: {
    color: C.accentDark,
    fontSize: 12,
    fontWeight: 800,
  },
  locationText: {
    color: C.text,
    fontSize: 14,
    fontWeight: 700,
  },
  infoStack: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    color: C.muted,
    fontSize: 14,
    lineHeight: 1.55,
  },
  viewBtn: {
    alignSelf: "flex-start",
    border: "none",
    background: C.accent,
    color: "#ffffff",
    borderRadius: 999,
    padding: "12px 16px",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
  },
  listCard: {
    display: "grid",
    gridTemplateColumns: "170px minmax(0, 1fr) auto",
    gap: 18,
    alignItems: "center",
    background: "#ffffff",
    borderRadius: 24,
    border: `1px solid ${C.border}`,
    boxShadow: C.shadow,
    padding: 16,
  },
  listMedia: {
    height: 140,
    borderRadius: 20,
    overflow: "hidden",
    background: "#252935",
  },
  listImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  listAvatar: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  listInitials: {
    color: "#ffffff",
    fontSize: 46,
    fontWeight: 900,
  },
  listBody: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    minWidth: 0,
  },
  profileTopRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  paginationBar: {
    marginTop: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "16px 18px",
    borderRadius: 22,
    background: "#ffffff",
    border: `1px solid ${C.border}`,
  },
  paginationBtn: {
    border: "none",
    background: C.accent,
    color: "#ffffff",
    borderRadius: 999,
    padding: "12px 16px",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
  },
  paginationBtnDisabled: {
    opacity: 0.45,
    cursor: "not-allowed",
  },
  paginationInfo: {
    color: C.muted,
    fontSize: 14,
    fontWeight: 800,
    textAlign: "center",
  },
};
